import { useDispatch } from 'react-redux';
import { setActivityDrawer } from '@store/appStore';
import { buildCatalogOrigin } from '@api/ActivityLogs/CatalogActivity';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, InputAdornment, Stack, TextField, Typography, Menu, MenuItem, IconButton } from '@mui/material';
import useOpenModal from '@hooks/useOpenModal';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { InputCategory } from '@api/InputCategories/InputCategory';
import InputCategoriesService from '@api/InputCategories/InputCategoryCtrl';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import { ModalSaveInputCategory } from '@screens/Cedis/components/ModalAddInputCategory';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';

const ListInputCategories: React.FunctionComponent = () => {
    const inputCategoriesService = useMemo(() => new InputCategoriesService(), []);
    const [inputCategories, setInputCategories] = useState<InputCategory[]>([]);
    const [currentInputCategory, setCurrentInputCategory] = useState<InputCategory | undefined>(undefined);
    const [filters, setFilters] = useState({
        search: ''
    });
    const modalSaveInputCategory = useOpenModal();
    const modalConfirmation = useConfirmationModal();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedCategory, setSelectedCategory] = useState<InputCategory | undefined>(undefined);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: InputCategory) => {
        setAnchorEl(event.currentTarget);
        setSelectedCategory(row);
    };

    const dispatch = useDispatch();

    /**
     * Abre la bitácora de este objeto (M2). Antes de esto, el catálogo escribía
     * renglones que **nadie podía ver**: no había puerta en ninguna de sus
     * pantallas.
     *
     * El snapshot se toma ANTES de cerrar el menú, que es donde se pierde la
     * selección, y el renglón de creación sale del propio documento (D8): no
     * cuesta una lectura.
     */
    const handleActivity = () => {
        const item = selectedCategory;
        handleMenuClose();
        if (!item?.id) return;
        dispatch(
            setActivityDrawer({
                isOpen: true,
                section: 'input-category',
                sectionId: item.id,
                derived: buildCatalogOrigin(item, 'creó la categoría', 'Categoría creada')
            })
        );
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedCategory(undefined);
    };

    const handleEdit = () => {
        handleMenuClose();
        if (selectedCategory) {
            setCurrentInputCategory(selectedCategory);
            modalSaveInputCategory.toggle();
        }
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        if (selectedCategory) {
            modalConfirmation.setModalData(
                'Eliminar categoría',
                `¿Estás seguro de que deseas eliminar la categoría "${selectedCategory.name}"? Esta acción no se puede deshacer.`
            );
        }
    };

    const handleDeleteConfirm = async () => {
        if (selectedCategory?.id) {
            showLoader();
            try {
                await inputCategoriesService.deleteInputCategory(selectedCategory.id);
                showAlertMessage('Categoría de insumo eliminada exitosamente');
                await getData();
            } catch (error) {
                showAlertError(error);
            }
            hideLoader();
        }
    };

    const getData = async () => {
        showLoader();
        try {
            const data = await inputCategoriesService.getInputCategories();
            setInputCategories(data);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onOpenModalAddInputCategory = () => {
        setCurrentInputCategory(undefined);
        modalSaveInputCategory.toggle();
    };

    const onSaveInputCategory = async (inputCategory: InputCategory) => {
        showLoader();
        try {
            await inputCategoriesService.saveInputCategory(inputCategory);
            showAlertMessage('Categoría de insumo guardada exitosamente');
            await getData();
            modalSaveInputCategory.toggle();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    const columns = useMemo<ResponsiveGridColDef<InputCategory>[]>(
        () => [
            {
                field: 'name',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Nombre
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.name}</Typography>
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        []
    );

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={6} />
                    <Grid item xs={12} md={3} textAlign="right">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onOpenModalAddInputCategory}
                            endIcon={<AddIcon />}
                        >
                            Nueva categoría
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar categoría"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <ResponsiveDataGrid
                searchValue={filters.search}
                rows={inputCategories}
                columns={columns}
                onCellDoubleClick={(cel) => {
                    setCurrentInputCategory(cel.row as InputCategory);
                    modalSaveInputCategory.toggle();
                }}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit}>Editar</MenuItem>
                <MenuItem onClick={handleActivity}>Actividad</MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    Eliminar
                </MenuItem>
            </Menu>
            <ModalSaveInputCategory
                modalSaveInputCategory={modalSaveInputCategory}
                inputCategory={currentInputCategory}
                onSaveInputCategory={onSaveInputCategory}
            />
            <ModalConfirmation modalConfirmation={modalConfirmation} onConfirmation={handleDeleteConfirm} />
        </Stack>
    );
};

export default ListInputCategories;
